import React, { useCallback, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Stack,
} from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import {
  getInterestCollectedByCustomerCurrentMonth,
  InterestCollectedByCustomerResponse,
  InterestCollectedByCustomerRow,
} from '../../../services/request/Reports';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { PageHeader, EmptyState } from '../../../shared-components/ui';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

const InterestCollectedCurrentMonth: React.FC = () => {
  const [data, setData] = useState<InterestCollectedByCustomerResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getInterestCollectedByCustomerCurrentMonth();
      const payload = response.data;
      if (payload) {
        setData(payload);
      } else {
        setError('No data returned');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const formatAmount = (value: number) => formatCurrency(value, 'INR', true);

  return (
    <Box>
      <PageHeader
        eyebrow="Lending · Reports"
        title="Interest collected this month"
        subtitle="Interest collected in the current month up to today, with each customer's last payment date."
        actions={
          <Button variant="contained" startIcon={<Refresh />} onClick={fetchReport} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
        }
      />

      {loading && <CircularProgress size={24} sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {data && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Card sx={{ minWidth: 180 }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Total Customers
                </Typography>
                <Typography variant="h6">{data.totalCustomers}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 220 }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Total Interest This Month (till today)
                </Typography>
                <Typography variant="h6">{formatAmount(data.totalInterestCollectedCurrentMonth)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 220 }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Total Expected Interest
                </Typography>
                <Typography variant="h6">{formatAmount(data.totalExpectedInterest ?? 0)}</Typography>
              </CardContent>
            </Card>
          </Stack>

          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Customer ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Interest collected (this month)</TableCell>
                  <TableCell align="right">Expected interest</TableCell>
                  <TableCell>Last payment date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((r: InterestCollectedByCustomerRow, idx: number) => (
                  <TableRow key={r.borrowerId}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{r.customerId || '—'}</TableCell>
                    <TableCell>{r.borrowerName || '—'}</TableCell>
                    <TableCell align="right">{formatAmount(r.interestCollectedCurrentMonth)}</TableCell>
                    <TableCell align="right">{formatAmount(r.expectedInterest ?? 0)}</TableCell>
                    <TableCell>{formatDate(r.lastPaymentDate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!loading && !error && data && data.rows.length === 0 && (
        <EmptyState title="No customer data" description="No interest collections recorded for the current month yet." />
      )}
    </Box>
  );
};

export default InterestCollectedCurrentMonth;
